import { svgPaths, facilitySvgPaths, menuSvgPaths, parkingSvgPaths, LogoContainer, EventContainer, MainContainer } from '../../imports/svgPaths';
import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { contactButtons } from '../../constants/menuData';
// import.*from.*imports/svg-8txpwpskyh";
import contactLogo from "../../image/contact/contact-logo.png";
import parallaxBg from "../../image/contact/contact-parallax-bg.jpg";
import { NaverDynamicMap } from '../common/NaverDynamicMap';

// Contact Section Title
function Container20() {
  return (
    <div
      className="box-border content-stretch flex flex-col gap-10 items-center justify-start leading-[0] not-italic p-0 relative shrink-0"
      data-name="Container"
    >
      <div className="font-paperlogy font-extrabold relative shrink-0 text-center text-neutral-50 text-[min(48px,_8vw)] tracking-[-0.96px]">
        <p className="block leading-[normal] font-bold">Contact Us</p>
      </div>
    </div>
  );
}

// Contact Info Card - 정확히 156px 높이
function ContactInfoCard() {
  return (
    <div className="backdrop-blur-sm bg-white/35 border border-[#999999] box-border content-stretch flex flex-col gap-4 items-center justify-center p-6 lg:p-8 relative rounded-2xl w-full h-[min(120px,_35%)] lg:h-[156px] shrink-0">
      <div className="flex items-center gap-2 text-neutral-950">
        <svg width="18" height="24" viewBox="0 0 18 24" fill="none">
          <path d={svgPaths.p2feee500} fill="#0C0C0C" />
        </svg>
        <span className="font-paperlogy font-medium text-[min(16px,_3vw)] tracking-[-0.32px]">
          대구시 달서구 조암남로32길 20-4 6층
        </span>
      </div>
      <div className="font-paperlogy font-bold text-[min(32px,_6vw)] lg:text-[36px] text-neutral-950 tracking-[-0.72px] whitespace-nowrap">
        053-642-5533
      </div>
    </div>
  );
}

// Contact Button Grid - 정확히 146px 높이
interface ContactButtonGridProps {
  onBusinessClick?: (inquiryType: string) => void;
}

function ContactButtonGrid({ onBusinessClick }: ContactButtonGridProps) {
  const handleButtonClick = (type: string) => {
    if (onBusinessClick) {
      // Map contact button types to inquiry types
      const inquiryTypeMap: Record<string, string> = {
        'business': 'business',
        'individual': 'reservation',
        'group': 'reservation',
        'content': 'content'
      };
      onBusinessClick(inquiryTypeMap[type] || 'business');
    }
  };

  return (
    <div className="bg-[#f0f0f0] box-border content-stretch grid grid-cols-2 relative rounded-2xl shrink-0 w-full overflow-hidden h-[min(120px,_37.5%)] lg:h-[146px]">
      {contactButtons.map((button, index) => (
        <button
          key={button.type}
          onClick={() => handleButtonClick(button.type)}
          className={`p-4 lg:p-6 text-[min(20px,_4vw)] text-neutral-950 hover:bg-gray-200 transition-colors font-paperlogy font-medium tracking-[-0.4px] ${
            index < 2 ? 'border-b border-[#dbdbdb]' : ''
          } ${
            index % 2 === 0 ? 'border-r border-[#dbdbdb]' : ''
          }`}
        >
          {button.label}
        </button>
      ))}
    </div>
  );
}

// Contact Logo Card - 정확히 66px 높이
function ContactLogoCard() {
  return (
    <div className="backdrop-blur-sm bg-white/35 border border-[#999999] box-border content-stretch flex items-center justify-center p-4 lg:p-5 relative rounded-2xl shrink-0 w-full h-[min(80px,_27.5%)] lg:h-[66px]">
      <div className="flex items-center gap-2 opacity-80">
        <div
          className="h-[min(20px,_4vw)] w-[min(120px,_25vw)] bg-center bg-contain bg-no-repeat mix-blend-overlay flex-shrink-0"
          style={{ backgroundImage: `url('${contactLogo}')` }}
        />
        <div className="text-[min(14px,_3vw)] text-black font-paperlogy font-bold tracking-[-0.32px] whitespace-nowrap">
          디지털 키즈카페
        </div>
      </div>
    </div>
  );
}

// Contact Content Container
interface Container21Props {
  onBusinessClick?: (inquiryType: string) => void;
}

function Container21({ onBusinessClick }: Container21Props) {
  return (
    <div
      className="box-border content-stretch flex flex-col lg:flex-row gap-4 lg:gap-[24px] items-center justify-center p-0 relative shrink-0 w-full max-w-[1080px] mx-auto"
      data-name="Container"
    >
      {/* Contact Map - 네이버 지도 600px */}
      <div className="w-full lg:w-[600px] h-[min(320px,_40vh)] lg:h-[400px] shrink-0">
        <NaverDynamicMap 
          width="100%" 
          height="100%" 
          className="shadow-lg"
        />
      </div>

      {/* Contact Info - 정확히 456px, 지도 높이와 맞춤 */}
      <div className="w-full lg:w-[456px] h-[min(320px,_40vh)] lg:h-auto flex flex-col gap-4 shrink-0">
        <ContactInfoCard />
        <ContactButtonGrid onBusinessClick={onBusinessClick} />
        <ContactLogoCard />
      </div>
    </div>
  );
}

// Contact Section
interface Section7Props {
  onBusinessClick?: (inquiryType: string) => void;
}

function Section7({ onBusinessClick }: Section7Props) {
  return (
    <div
      className="box-border content-stretch flex flex-col gap-20 items-center justify-start pb-0 pt-20 px-0 relative shrink-0 w-full"
      data-name="Section"
    >
      <Container20 />
      <Container21 onBusinessClick={onBusinessClick} />
    </div>
  );
}

interface ContactSectionProps {
  onBusinessClick?: (inquiryType: string) => void;
}

export function ContactSection({ onBusinessClick }: ContactSectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // 패럴랙스 효과: 스크롤에 따라 배경이 반대 방향으로 움직임 (스크롤 내릴 때 배경은 위로)
  const y = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);

  return (
    <div
      ref={ref}
      className="bg-[#999999] relative shrink-0 w-full"
      data-name="Main Container"
    >
      <div className="overflow-clip relative size-full">
        {/* 패럴랙스 배경 이미지 */}
        <motion.div
          className="absolute inset-0 w-full h-full"
          style={{ y }}
        >
          <div
            className="absolute inset-0 w-full h-[120%] bg-cover bg-center bg-no-repeat opacity-50"
            style={{
              backgroundImage: `url(${parallaxBg})`,
              transform: "translateY(-10%)"
            }}
          />
        </motion.div>
        {/* 오버레이 그라데이션 - 기존 #999999 색상과 조화 */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-600/70 via-gray-600/50 to-gray-600/70" />

        <div className="box-border content-stretch flex flex-col gap-2.5 items-start justify-start left-1/2 px-4 py-[120px] relative translate-x-[-50%] w-[min(1280px,_calc(100vw_-_40px))] z-10">
          <Section7 onBusinessClick={onBusinessClick} />
        </div>
      </div>
    </div>
  );
}